import { Link } from "react-router-dom";
import { useLocalePath, useT } from "../i18n";
import { coverImage, formatMileage, formatPrice, priceNote } from "../lib/format";
import { ResponsiveImage } from "./ResponsiveImage";

export const StatusBadge = ({ status, inline = false }) => {
  const t = useT();
  if (status !== "reserved" && status !== "sold") return null;
  const style = status === "sold" ? "bg-white px-4 py-2 text-[12px] font-semibold tracking-[0.24em] text-ink shadow-[0_2px_18px_rgba(0,0,0,0.35)]" : "border border-white/40 bg-ink/70 text-white backdrop-blur-md";
  const pos = inline ? "inline-block" : "absolute left-4 top-4 z-10";
  return (
    <span data-testid={`status-badge-${status}`} className={`status-badge ${pos} px-3 py-1.5 ${style}`}>
      {t(`enums.status.${status}`)}
    </span>
  );
};

export const VehicleCard = ({ vehicle, sizes, tone = "dark", aspect = "aspect-[3/2]", titleClass = "text-2xl", eager = false, maxVariant = "large" }) => {
  const t = useT();
  const lp = useLocalePath();
  const img = coverImage(vehicle);
  const line = tone === "dark" ? "border-white/15" : "border-ink/15";
  const specs = [
    vehicle.model_year,
    formatMileage(vehicle.mileage_km),
    vehicle.drivetrain && t(`enums.drivetrain.${vehicle.drivetrain}`),
  ].filter(Boolean);
  return (
    <Link to={lp(`inventory/${vehicle.slug}`)} data-testid={`vehicle-card-${vehicle.stock_id}`} className="group block">
      <div className={`relative overflow-hidden ${tone === "dark" ? "bg-graphite" : "bg-mist"} ${aspect}`}>
        {img ? (
          <ResponsiveImage
            image={img}
            sizes={sizes}
            eager={eager}
            maxVariant={maxVariant}
            className={`card-image absolute inset-0 h-full w-full object-cover ${vehicle.status === "sold" ? "opacity-70 grayscale" : ""}`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" data-testid={`vehicle-noimage-${vehicle.stock_id}`}>
            <span className="micro-label opacity-40">{t("detail.noImages")}</span>
          </div>
        )}
        <StatusBadge status={vehicle.status} />
      </div>
      <div className="pt-4">
        <div className="flex items-baseline justify-between gap-4">
          <p className="micro-label opacity-60" data-testid={`vehicle-make-${vehicle.stock_id}`}>{vehicle.make}</p>
          <p className="micro-label tabular-nums opacity-35" data-testid={`vehicle-stock-${vehicle.stock_id}`}>{vehicle.stock_id}</p>
        </div>
        <h3 className={`card-title font-display mt-1.5 leading-[1.12] ${titleClass}`} data-testid={`vehicle-title-${vehicle.stock_id}`}>
          {vehicle.model} <span className="opacity-55">{vehicle.grade}</span>
        </h3>
        <div className={`mt-3.5 flex items-baseline justify-between gap-5 border-t pt-3 ${line}`}>
          <p className="card-specs min-w-0 tabular-nums opacity-70" data-testid={`vehicle-meta-${vehicle.stock_id}`}>
            {specs.map((s, i) => (
              <span key={i} className="whitespace-nowrap">
                {i > 0 && <span className="mx-2 opacity-40" aria-hidden>·</span>}
                {s}
              </span>
            ))}
          </p>
          <p className="card-price shrink-0 whitespace-nowrap tabular-nums" data-testid={`vehicle-price-${vehicle.stock_id}`}>
            {formatPrice(vehicle, t)}
            {vehicle.price?.kind === "japan" && vehicle.price.amount != null && <span className="ml-1 text-[11px] opacity-60" data-testid={`vehicle-price-excl-label-${vehicle.stock_id}`}>{t("price.exclTax")}</span>}
          </p>
        </div>
        {priceNote(vehicle, t) && vehicle.price?.kind !== "japan" && (
          <p className="mt-1 text-right text-[11px] tracking-wide opacity-60" data-testid={`vehicle-price-note-${vehicle.stock_id}`}>{priceNote(vehicle, t)}</p>
        )}
        {vehicle.price?.kind === "japan" && vehicle.price.amount != null && (
          <div className="mt-0.5 text-right text-[11px] tabular-nums tracking-wide opacity-50" data-testid={`vehicle-price-secondary-${vehicle.stock_id}`}>
            <p>¥{vehicle.price.vehicle_price_jpy?.toLocaleString("en-US")} {t("price.inclTax")}</p>
            <p>{t("price.totalLabel")} ¥{vehicle.price.total_payment_jpy?.toLocaleString("en-US")}</p>
          </div>
        )}
      </div>
    </Link>
  );
};

export const VehicleCardSkeleton = ({ tone = "dark", aspect = "aspect-[3/2]" }) => (
  <div aria-hidden className="animate-pulse">
    <div className={`${aspect} ${tone === "dark" ? "bg-white/5" : "bg-ink/5"}`} />
    <div className={`mt-5 h-3 w-16 ${tone === "dark" ? "bg-white/10" : "bg-ink/10"}`} />
    <div className={`mt-3 h-6 w-2/3 ${tone === "dark" ? "bg-white/10" : "bg-ink/10"}`} />
  </div>
);
